import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  LinearProgress,
} from "@mui/material";
import { Add, Delete, Edit, Refresh } from "@mui/icons-material";
import { TablePagination } from "@mui/material";

/**
 * PremiosAdmin.jsx
 * - Arriba: CRUD de catálogo de Premios contra /api/premios
 * - Abajo: tabla de asignaciones (colegiados_premios) con alta/borra
 * - Asignación masiva pegando códigos
 * - NUEVO: muestra "Nombre completo" al lado del código de colegiado
 */

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: false,
});

// Adjunta Authorization si hay JWT en localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Log de errores para depurar más fácil
api.interceptors.response.use(
  (res) => res,
  (err) => {
    console.error("API error:", err?.response || err?.message || err);
    return Promise.reject(err);
  }
);

// 🔹 Normaliza Page de Spring (camelCase o snake_case) o array plano
const getPageMeta = (data) => ({
  content: Array.isArray(data?.content)
    ? data.content
    : Array.isArray(data)
      ? data
      : [],
  totalElements:
    typeof data?.totalElements === "number"
      ? data.totalElements
      : typeof data?.total_elements === "number"
        ? data.total_elements
        : 0,
  number:
    typeof data?.number === "number"
      ? data.number
      : typeof data?.page === "number"
        ? data.page
        : 0,
  size:
    typeof data?.size === "number"
      ? data.size
      : typeof data?.page_size === "number"
        ? data.page_size
        : 10,
});

const initialPremio = { nombre: "", observacion: "" };

// Helpers para código y nombre
const getCodigoFromAsignacion = (a) =>
  a?.colegiadoCodigo ??
  a?.colegiado_codigo ??
  a?.colegiadoId ??
  a?.colegiado_id ??
  null;

const nombreCompleto = (c) =>
  c ? [c.nombre, c.apellido_1, c.apellido_2].filter(Boolean).join(" ") : "—";

export default function PremiosAdmin() {
  const [premios, setPremios] = useState([]);
  const [loadingPremios, setLoadingPremios] = useState(false);
  const [openDlg, setOpenDlg] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialPremio);

  // Asignaciones
  const [asignaciones, setAsignaciones] = useState([]);
  const [loadingAsig, setLoadingAsig] = useState(false);
  const [asigPage, setAsigPage] = useState(0);
  const [asigRowsPerPage, setAsigRowsPerPage] = useState(10);
  const [asigTotal, setAsigTotal] = useState(0);
  const [asigSort] = useState({ sortBy: "fechaConcesion", order: "desc" });
  const [asigFilters] = useState({ codigo: "", premioId: "" });
  const [asigForm, setAsigForm] = useState({
    colegiadoCodigo: "",
    premioId: "",
    fechaConcesion: new Date().toISOString().slice(0, 10),
  });

  // Estados para asignación masiva
  const [bulkText, setBulkText] = useState("");
  const [bulkCodes, setBulkCodes] = useState([]); // números deduplicados
  const [bulkParsingErr, setBulkParsingErr] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState({ ok: 0, ko: 0, errors: [] });

  // Nombres de colegiados por código (cache + estado derivado)
  const colegiadoCache = useRef(new Map()); // codigo -> {nombre, apellido_1, apellido_2, ...} | null
  const [colegiadoDetalles, setColegiadoDetalles] = useState({}); // para forzar repaint

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const handleSnack = (msg, sev = "success") =>
    setSnack({ open: true, msg, sev });

  const premioById = useMemo(() => {
    const map = new Map();
    premios.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [premios]);

  // --- Fetchers ---
  const fetchPremios = async () => {
    setLoadingPremios(true);
    try {
      const { data } = await api.get("/api/premios");
      setPremios(Array.isArray(data) ? data : data?.content ?? []);
    } catch (e) {
      handleSnack(
        `Error cargando premios: ${e?.response?.status || ""}`,
        "error"
      );
    } finally {
      setLoadingPremios(false);
    }
  };

  const fetchAsignaciones = async ({
    page = asigPage,
    size = asigRowsPerPage,
    sortBy = asigSort.sortBy,
    order = asigSort.order,
    codigo = asigFilters.codigo,
    premioId = asigFilters.premioId,
  } = {}) => {
    setLoadingAsig(true);
    try {
      const params = { page, size, sortBy, order };
      if (codigo) params.codigo = codigo;
      if (premioId) params.premioId = premioId;

      const { data } = await api.get("/api/admin/colegiados-premios", {
        params,
      });
      const meta = getPageMeta(data);

      setAsignaciones(meta.content);
      setAsigTotal(meta.totalElements);
      setAsigPage(meta.number);
      setAsigRowsPerPage(meta.size);
    } catch (e) {
      handleSnack(
        `Error cargando asignaciones: ${e?.response?.status || ""}`,
        "error"
      );
    } finally {
      setLoadingAsig(false);
    }
  };

  useEffect(() => {
    fetchPremios();
    fetchAsignaciones({ page: 0 });
  }, []);

  // --- CRUD Premios ---
  const openCreate = () => {
    setEditId(null);
    setForm(initialPremio);
    setOpenDlg(true);
  };

  const openEdit = (premio) => {
    setEditId(premio.id);
    setForm({
      nombre: premio.nombre ?? "",
      observacion: premio.observacion ?? "",
    });
    setOpenDlg(true);
  };

  const savePremio = async () => {
    const payload = { ...form };
    if (!payload.nombre?.trim()) {
      handleSnack("El nombre es obligatorio", "warning");
      return;
    }
    try {
      if (editId == null) {
        await api.post("/api/premios", payload);
        handleSnack("Premio creado");
      } else {
        await api.put(`/api/premios/${editId}`, payload);
        handleSnack("Premio actualizado");
      }
      setOpenDlg(false);
      await fetchPremios();
    } catch (e) {
      console.error(e);
      handleSnack("No se pudo guardar el premio", "error");
    }
  };

  const deletePremio = async (id) => {
    if (!window.confirm("¿Eliminar este premio?")) return;
    try {
      await api.delete(`/api/premios/${id}`);
      handleSnack("Premio eliminado");
      await fetchPremios();
    } catch (e) {
      console.error(e);
      handleSnack("No se pudo eliminar", "error");
    }
  };

  // --- Asignaciones individuales ---
  const createAsignacion = async () => {
    const { colegiadoCodigo, premioId /*, fechaConcesion*/ } = asigForm;
    if (!colegiadoCodigo || !premioId) {
      handleSnack("Rellena colegiado y premio", "warning");
      return;
    }
    try {
      // PUT /api/admin/{codigo}/premios/{premioId}
      await api.put(
        `/api/admin/${Number(colegiadoCodigo)}/premios/${Number(premioId)}`
      );
      handleSnack("Premio concedido");
      setAsigForm((s) => ({ ...s, colegiadoCodigo: "" }));
      await fetchAsignaciones();
    } catch (e) {
      console.error(e);
      handleSnack("No se pudo conceder el premio", "error");
    }
  };

  const deleteAsignacion = async (colegiadoCodigo, premioId) => {
    if (!window.confirm("¿Quitar esta concesión?")) return;
    try {
      // DELETE /api/admin/{codigo}/premios/{premioId}
      await api.delete(
        `/api/admin/${Number(colegiadoCodigo)}/premios/${Number(premioId)}`
      );
      handleSnack("Concesión eliminada");
      await fetchAsignaciones();
    } catch (e) {
      console.error(e);
      handleSnack("No se pudo eliminar la concesión", "error");
    }
  };

  // --- Exportación tabla concesiones ---
  const toCsvAsignaciones = (list) => {
    const header = ["Coleg. código", "Premio ID", "Premio", "Fecha concesión"];
    const lines = (list || []).map((a) => {
      const premio =
        premioById.get(String(a?.premioId)) ||
        premioById.get(String(a?.premio_id));
      const premioNombre = premio?.nombre ?? "";
      const cod =
        a?.colegiadoCodigo ??
        a?.colegiado_codigo ??
        a?.colegiadoId ??
        a?.colegiado_id ??
        "";
      const pid = a?.premioId ?? a?.premio_id ?? "";
      const fecha =
        (a?.fechaConcesion ?? a?.fecha_concesion ?? a?.fecha ?? "")
          ?.toString()
          ?.slice(0, 10) || "";
      const vals = [cod, pid, premioNombre, fecha];
      return vals.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";");
    });
    return "\uFEFF" + [header.join(";"), ...lines].join("\n");
  };

  const exportAsignaciones = async () => {
    const pageSizeBulk = 1000;
    let page = 0;
    let total = Infinity;
    let acc = [];
    try {
      while (acc.length < total) {
        const params = {
          page,
          size: pageSizeBulk,
          sortBy: asigSort.sortBy,
          order: asigSort.order,
        };
        if (asigFilters.codigo) params.codigo = asigFilters.codigo;
        if (asigFilters.premioId) params.premioId = asigFilters.premioId;

        const { data } = await api.get("/api/admin/colegiados-premios", {
          params,
        });
        const meta = getPageMeta(data);

        acc = acc.concat(meta.content);
        total = meta.totalElements;

        if (meta.content.length < pageSizeBulk) break;
        page += 1;
      }
    } catch (e) {
      handleSnack("No se pudo exportar las concesiones", "error");
      return;
    }
    const csv = toCsvAsignaciones(acc);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "colegiados_premios.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // --- Parsing y asignación masiva ---
  const parseBulk = (raw) => {
    const txt = (raw || "").replace(/[;\t]/g, ",").trim();
    const parts = txt
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
    const nums = [];
    const seen = new Set();
    for (const p of parts) {
      const onlyDigits = p.replace(/[^\d]/g, "");
      if (!onlyDigits) continue;
      const n = Number(onlyDigits);
      if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
        seen.add(n);
        nums.push(n);
      }
    }
    return nums;
  };

  useEffect(() => {
    const codes = parseBulk(bulkText);
    setBulkCodes(codes);
    setBulkParsingErr(
      bulkText && codes.length === 0 ? "No hay códigos válidos" : ""
    );
  }, [bulkText]);

  const assignBulk = async () => {
    const premioId = asigForm.premioId;
    if (!premioId) {
      handleSnack("Selecciona un premio para la asignación masiva", "warning");
      return;
    }
    if (bulkCodes.length === 0) {
      handleSnack("No hay códigos para procesar", "warning");
      return;
    }
    setBulkRunning(true);
    setBulkProgress(0);
    setBulkResult({ ok: 0, ko: 0, errors: [] });

    try {
      const chunkSize = 25; // batch controlado
      let ok = 0;
      let ko = 0;
      let errors = [];
      for (let i = 0; i < bulkCodes.length; i += chunkSize) {
        const chunk = bulkCodes.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
          chunk.map((codigo) =>
            api.put(`/api/admin/${codigo}/premios/${Number(premioId)}`)
          )
        );
        results.forEach((r, idx) => {
          if (r.status === "fulfilled") ok++;
          else {
            ko++;
            const codigo = chunk[idx];
            const status = r?.reason?.response?.status;
            const msg =
              r?.reason?.response?.data?.message ||
              r?.reason?.message ||
              "Error";
            errors.push({ codigo, status, msg: String(msg) });
          }
        });
        setBulkProgress(
          Math.round(((i + chunk.length) / bulkCodes.length) * 100)
        );
      }
      setBulkResult({ ok, ko, errors });
      handleSnack(
        `Asignación masiva finalizada: ${ok} ok, ${ko} errores`,
        ko > 0 ? "warning" : "success"
      );
      await fetchAsignaciones();
    } catch (e) {
      console.error(e);
      handleSnack("Error en la asignación masiva", "error");
    } finally {
      setBulkRunning(false);
      setBulkProgress(100);
    }
  };

  // =========================
  // NUEVO: detalles colegiado
  // =========================
  const fetchColegiadoDetalles = async (codigoColegiado) => {
    if (!codigoColegiado) return null;
    if (colegiadoCache.current.has(codigoColegiado)) {
      return colegiadoCache.current.get(codigoColegiado);
    }
    try {
      const { data } = await api.get(`/api/admin/colegiados/${codigoColegiado}`);
      const c = {
        nombre: data?.nombre ?? "",
        apellido_1: data?.apellido_1 ?? "",
        apellido_2: data?.apellido_2 ?? "",
        localidad: data?.localidad ?? "",
        provincia: data?.provincia ?? "",
      };
      colegiadoCache.current.set(codigoColegiado, c);
      return c;
    } catch {
      colegiadoCache.current.set(codigoColegiado, null);
      return null;
    }
  };

  // Cargar nombres cuando cambien las asignaciones
  useEffect(() => {
    const load = async () => {
      const codes = Array.from(
        new Set(
          (asignaciones || [])
            .map(getCodigoFromAsignacion)
            .filter((c) => c != null)
        )
      );

      // Fetch solo los que no estén en caché
      const toFetch = codes.filter((c) => !colegiadoCache.current.has(c));
      if (toFetch.length > 0) {
        await Promise.all(toFetch.map((c) => fetchColegiadoDetalles(c)));
      }

      // Refresca estado derivado para forzar re-render
      const next = {};
      codes.forEach((c) => (next[c] = colegiadoCache.current.get(c)));
      setColegiadoDetalles(next);
    };

    if (asignaciones.length > 0) load();
  }, [asignaciones]);

  return (
    <Box p={2} className="space-y-6">
      {/* --- CABECERA --- */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" fontWeight={700}>
          Premios (catálogo)
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
            Nuevo
          </Button>
          <IconButton onClick={fetchPremios} title="Refrescar" aria-label="refrescar">
            <Refresh />
          </IconButton>
        </Stack>
      </Stack>

      {/* --- TABLA PREMIOS --- */}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={80}>ID</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Observación</TableCell>
              <TableCell align="right" width={140}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {premios.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.id}</TableCell>
                <TableCell>{p.nombre}</TableCell>
                <TableCell>{p.observacion}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(p)} title="Editar">
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deletePremio(p.id)}
                    title="Eliminar"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {premios.length === 0 && !loadingPremios && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Sin datos
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* --- FORM ASIGNACIONES --- */}
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" fontWeight={700}>
          Concesión de premios (Premios de Colegiados)
        </Typography>
        <Button variant="outlined" onClick={exportAsignaciones}>
          Exportar
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Asignación de premios
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
          <TextField
            label="Pega aquí los códigos (columna o comas)"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            placeholder={`Ejemplos:\n3\n9210\n9211\n9212\n...\n\nó\n3,9210,9211,9212`}
          />
          <Stack spacing={1} sx={{ minWidth: 280 }}>
            <FormControl>
              <InputLabel id="premioId-bulk-label">Premio</InputLabel>
              <Select
                labelId="premioId-bulk-label"
                label="Premio"
                value={asigForm.premioId}
                onChange={(e) => setAsigForm((s) => ({ ...s, premioId: e.target.value }))}
              >
                {premios.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disabled={bulkRunning || !asigForm.premioId || bulkCodes.length === 0}
              onClick={assignBulk}
            >
              Conceder ({bulkCodes.length})
            </Button>
            {bulkRunning && (
              <Box>
                <LinearProgress variant="determinate" value={bulkProgress} />
                <Typography variant="caption" color="text.secondary">
                  Procesando… {bulkProgress}%
                </Typography>
              </Box>
            )}
            {bulkParsingErr && <Alert severity="warning">{bulkParsingErr}</Alert>}
            {bulkCodes.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`${bulkCodes.length} códigos válidos`} />
                <Chip
                  label={`Preview: ${bulkCodes.slice(0, 20).join(", ")}${bulkCodes.length > 20 ? "…" : ""
                    }`}
                  variant="outlined"
                />
              </Stack>
            )}
            {(bulkResult.ok > 0 || bulkResult.ko > 0) && !bulkRunning && (
              <Alert severity={bulkResult.ko ? "warning" : "success"}>
                Resultado: {bulkResult.ok} ok, {bulkResult.ko} errores
              </Alert>
            )}
          </Stack>
        </Stack>
        {bulkResult.errors?.length > 0 && !bulkRunning && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Errores (máx. 50 mostrados):
            </Typography>
            <Paper variant="outlined" sx={{ maxHeight: 160, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={120}>Código</TableCell>
                    <TableCell width={120}>HTTP</TableCell>
                    <TableCell>Mensaje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkResult.errors.slice(0, 50).map((e, i) => (
                    <TableRow key={`${e.codigo}-${i}`}>
                      <TableCell>{e.codigo}</TableCell>
                      <TableCell>{e.status ?? "-"}</TableCell>
                      <TableCell>{e.msg}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
      </Paper>

      {/* --- TABLA ASIGNACIONES --- */}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={140}>Coleg. código</TableCell>
              <TableCell width={260}>Nombre completo</TableCell>
              <TableCell>Premio</TableCell>
              <TableCell width={180}>Fecha concesión</TableCell>
              <TableCell align="right" width={120}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {asignaciones.map((a, idx) => {
              const premio =
                premioById.get(String(a.premioId)) ||
                premioById.get(String(a.premio_id));
              const premioNombre = premio?.nombre ?? "-";
              const cod = getCodigoFromAsignacion(a);
              const pid = a.premioId ?? a.premio_id;
              const fecha = a.fechaConcesion ?? a.fecha_concesion ?? a.fecha;

              // Detalles del colegiado desde estado (con caché detrás)
              const detalles = cod != null ? colegiadoDetalles[cod] : null;
              const mostrarNombre =
                detalles === undefined ? "…" : nombreCompleto(detalles);

              return (
                <TableRow key={`${cod}-${pid}-${idx}`} hover>
                  <TableCell>{cod}</TableCell>
                  <TableCell>{mostrarNombre}</TableCell>
                  <TableCell>{premioNombre}</TableCell>
                  <TableCell>{fecha ? String(fecha).slice(0, 10) : "-"}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteAsignacion(cod, pid)}
                      title="Eliminar"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {asignaciones.length === 0 && !loadingAsig && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Sin datos
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={asigTotal}
          page={asigPage}
          onPageChange={(_, p) => {
            setAsigPage(p);
            fetchAsignaciones({ page: p });
          }}
          rowsPerPage={asigRowsPerPage}
          onRowsPerPageChange={(e) => {
            const size = parseInt(e.target.value, 10);
            setAsigRowsPerPage(size);
            setAsigPage(0);
            fetchAsignaciones({ page: 0, size });
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* --- DIALOGO CREAR/EDITAR PREMIO --- */}
      <Dialog open={openDlg} onClose={() => setOpenDlg(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId == null ? "Nuevo premio" : `Editar premio #${editId}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))}
              autoFocus
              required
            />
            <TextField
              label="Observación"
              value={form.observacion}
              onChange={(e) => setForm((s) => ({ ...s, observacion: e.target.value }))}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDlg(false)}>Cancelar</Button>
          <Button variant="contained" onClick={savePremio}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.sev}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
